<template>
  <CarColourAndExtras
    :disable="savingColourAndExtras || isExistingQuoteOpen"
    :loading="loadingColoursAndExtras"
    :saving="savingColourAndExtras"
    :errors="$v"
    :exterior-colours="coloursAndExtras.exteriorColours"
    :interior-colours="coloursAndExtras.interiorColours"
    :optional-equipment="coloursAndExtras.optionalEquipment"
    :included-equipment="coloursAndExtras.includedEquipment"
    :selected-exterior-colour="selectedExteriorColour"
    :selected-interior-colour="selectedInteriorColour"
    :show-add-optional-extra="showAddOptionalExtra"
    :show-temp-adhoc-extra="showTempAdhocExtra"
    :selected-optional-equipment="selectedOptionalEquipment"
    :selected-choices="Object.values(selectedChoices)"
    :adhoc-equipment="customerOptions.adhocEquipment"
    :temp-adhoc-equipment="tempAdhocEquipment"
    :maximum-adhoc-equipment="$Constants.MAXIMUM_ADHOC_VEHICLE_OPTIONS"
    :all-precluded="allPrecluded"
    :all-required="allRequired"
    @update:exterior-colour="setExteriorColour"
    @update:exterior-colour-name="
      updateColourName('selectedExteriorColour', $event)
    "
    @update:interior-trim="setInteriorColour($event)"
    @update:interior-trim-name="
      updateColourName('selectedInteriorColour', $event)
    "
    @show:add-optional-extra="showTempOptionalExtra"
    @add:new-optional-equipment="addNewOptionalEquipment"
    @update:existing-optional-equipment="updateOptionalEquipment"
    @remove:optional-equipment="removeOptionalEquipment"
    @add:temp-adhoc-extra="addAdhocEquipment"
    @update:temp-adhoc-extra="tempAdhocEquipment = $event"
    @remove:temp-adhoc-extra="removeTempAdhocExtra"
    @update:adhoc-equipment="updateAdhocEquipment"
    @remove:adhoc-equipment="removeAdhocEquipment"
    @next="next"
  />
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import validations from './validations';
import {
  getAllRequired,
  getChoiceRequired,
  flattenColorsAndExtras,
  getPrecludedFromList,
  getNonChoiceRequired,
  findIdInList,
  findIndexInList,
  mapToOption
} from '@/utils/colour-and-extras';
import { DEFAULT_COLOUR_AND_EXTRA } from '@/utils/constants';

export default {
  name: 'CarColourAndExtrasWidget',
  metaInfo: {
    title: 'Colour and Extras |'
  },
  data() {
    return {
      updateApplicationPaths:
        this.$Constants.APPLICATION_UPDATE_PATHS.carColourAndExtras,
      selectedExteriorColour: { ...DEFAULT_COLOUR_AND_EXTRA },
      selectedInteriorColour: { ...DEFAULT_COLOUR_AND_EXTRA },
      selectedOptionalEquipment: [],
      selectedChoices: {},
      tempAdhocEquipment: undefined,
      showAddOptionalExtra: false,
      showTempAdhocExtra: false
    };
  },
  validations,
  computed: {
    ...mapState({
      quote: (state) => state.quote.quote,
      loadingColoursAndExtras: (state) => state.quote.loadingColoursAndExtras,
      savingColourAndExtras: (state) =>
        state.application.applicationSaving || state.quote.savingQuote,
      coloursAndExtras: (state) => state.quote.coloursAndExtras || {},
      application: (state) => state.application.application,
      customerOptions: (state) =>
        state.application.application.metaData.customerOptions
    }),
    flatColourAndExtras() {
      return flattenColorsAndExtras(this.coloursAndExtras);
    },
    allPrecluded() {
      let included = this.coloursAndExtras.includedEquipment || [];
      let obj = getPrecludedFromList([
        this.selectedExteriorColour,
        this.selectedInteriorColour,
        ...included,
        ...Object.values(this.allRequired),
        ...Object.values(this.selectedChoices),
        ...this.selectedOptionalEquipment
      ]);
      Object.values(this.allRequired).forEach((item) => {
        obj[item.id] = item.id;
      });
      return obj;
    },
    allRequired() {
      let equipment = [].concat(
        this.selectedOptionalEquipment || [],
        Object.values(this.selectedChoices) || []
      );
      const optionalEquipmentRequired = equipment.reduce(
        (accumulator, iterate) => {
          return accumulator.concat(getNonChoiceRequired(iterate));
        },
        []
      );
      return getAllRequired(
        [
          ...getNonChoiceRequired(this.selectedExteriorColour),
          ...getNonChoiceRequired(this.selectedInteriorColour),
          ...optionalEquipmentRequired
        ],
        this.flatColourAndExtras
      );
    },
    ...mapGetters('application', ['customerSelectionIsNew']),
    ...mapGetters('quote', ['isExistingQuoteOpen'])
  },
  methods: {
    ...mapMutations('application', ['updateCustomerOption']),
    ...mapActions('quote', ['getColoursAndExtras', 'createQuote']),
    /**
     * Clears and hides the temporary unsaved adhoc equipment input
     */
    removeTempAdhocExtra() {
      this.tempAdhocEquipment = undefined;
      this.showTempAdhocExtra = false;
    },
    /**
     * Adds the temporary adhoc input value as an 'adhocEquipment' to the customer options.
     * @param hide If true, this will hide the temporary adhoc input field
     */
    addAdhocEquipment(hide) {
      if (this.tempAdhocEquipment) {
        let arr = [
          ...(this.customerOptions.adhocEquipment || []),
          this.tempAdhocEquipment
        ];
        this.updateCustomerOption({
          field: 'adhocEquipment',
          value: arr
        });
        this.tempAdhocEquipment = undefined;
      }
      this.showTempAdhocExtra = !hide;
      this.showAddOptionalExtra = false;
    },
    /**
     * Updates the value of an adhoc equipment at a given index
     * @param value The new adhoc equipment value
     * @param index The index of the adhoc equipment to be updated
     */
    updateAdhocEquipment({ value, index }) {
      let arr = [...(this.customerOptions.adhocEquipment || [])];
      arr[index] = value;
      this.updateCustomerOption({
        field: 'adhocEquipment',
        value: arr
      });
    },
    /**
     * Removes an adhoc equipment at a given index
     * @param index The index of the adhoc equipment to remove
     */
    removeAdhocEquipment(index) {
      let arr = [...(this.customerOptions.adhocEquipment || [])];
      arr.splice(index, 1);
      this.updateCustomerOption({
        field: 'adhocEquipment',
        value: arr
      });
    },
    /**
     * Set the name property for an exterior / interior colour. This is used in the case that no exterior
     * or interior colours are available to be picked
     * @param key One of ['selectedExteriorColour', 'selectedInteriorColour']
     * @param name The name of the colour to set
     */
    updateColourName(key, name) {
      this[key] = name ? { ...DEFAULT_COLOUR_AND_EXTRA, name } : undefined;
    },
    /**
     * Sets the exterior colour option.
     * @param colour The colour to set
     */
    setExteriorColour(colour) {
      colour = colour || undefined;
      const removedEquipment = { ...this.selectedExteriorColour };
      this.selectedExteriorColour = colour;
      this.setRequiredInData(colour);
      this.removeOptionsNotRequiredFromEquipment(removedEquipment);
      if (!colour) {
        this.setInteriorColour(undefined);
      }
    },
    /**
     * Sets the interior colour option.
     * @param colour The colour to set
     */
    setInteriorColour(colour) {
      colour = colour || undefined;
      const removedEquipment = { ...this.selectedInteriorColour };
      this.selectedInteriorColour = colour;
      this.setRequiredInData(colour);
      this.removeOptionsNotRequiredFromEquipment(removedEquipment);
    },
    /**
     * Show the form field to add an optional extra. If the temporary adhoc input has a value, it will be
     * saved as an adhoc equipment.
     */
    showTempOptionalExtra() {
      if (this.tempAdhocEquipment) {
        this.addAdhocEquipment();
      }
      this.showAddOptionalExtra = true;
      this.showTempAdhocExtra = false;
    },
    /**
     * Removes an optional equipment at a given index
     * @param index The index of the optional equipment to remove
     */
    removeOptionalEquipment(index) {
      let arr = [...this.selectedOptionalEquipment];
      const removedEquipment = { ...arr[index] };
      arr.splice(index, 1);
      this.selectedOptionalEquipment = arr;
      this.removeChoiceRequired(removedEquipment);
      this.removeOptionsNotRequiredFromEquipment(removedEquipment);
    },
    /**
     * Adds a new optional equipment
     * @param equipment
     */
    addNewOptionalEquipment(equipment) {
      this.selectedOptionalEquipment.push(equipment);
      this.setRequiredInData(equipment);
      this.showChoiceRequired(equipment);
      this.showAddOptionalExtra = false;
    },
    /**
     * Updates an optional equipment at a given index
     * @param index The index of the optional equipment to update
     * @param update The new optional equipment
     */
    updateOptionalEquipment({ index, update }) {
      let arr = [...this.selectedOptionalEquipment];
      const removedEquipment = { ...arr[index] };
      arr[index] = update;
      this.selectedOptionalEquipment = arr;
      this.removeChoiceRequired(removedEquipment);
      this.setRequiredInData(update);
      this.showChoiceRequired(update);
      this.removeOptionsNotRequiredFromEquipment(removedEquipment);
    },
    /**
     * Display UI to require a user to select an optional equipment from a list if the given optional equipment
     * requires it.
     * @param equipment The optional equipment to determine if a choice requirement is necessary
     * @returns {Promise<void>}
     */
    async showChoiceRequired(equipment) {
      let required = getChoiceRequired(equipment, this.flatColourAndExtras);
      if (required.length) {
        for (let i = 0; i < required.length; i++) {
          await new Promise((resolve) => {
            this.$q
              .dialog({
                title: 'Options',
                message: 'Please choose from one of the required options',
                cancel: false,
                persistent: true,
                options: {
                  model: undefined,
                  items: required[i].map((value) => ({
                    label: value.name,
                    value: value.id
                  }))
                }
              })
              .onOk((data) => {
                this.selectChoiceRequired(
                  this.flatColourAndExtras[data],
                  equipment.id
                );
                resolve();
              });
          });
        }
      }
    },
    /**
     * Set the selected choice as key (ID of the requiring equipment) value (the selected equipment)
     * @param equipment The selected equipment
     * @param requiredBy The ID of the equipment that required the selection
     */
    selectChoiceRequired(equipment, requiredBy) {
      this.selectedChoices = {
        ...this.selectedChoices,
        [requiredBy]: equipment
      };
    },
    /**
     * Remove any choice required from the given equipment
     * @param equipment The equipment to remove any choice required from
     */
    removeChoiceRequired(equipment) {
      if (this.selectedChoices.hasOwnProperty(equipment.id)) {
        let choices = { ...this.selectedChoices };
        delete choices[equipment.id];
        this.selectedChoices = choices;
      }
    },
    /**
     * Remove any selected equipment from a given list IF they are not required by any other equipment.
     * @param removedEquipment
     */
    removeOptionsNotRequiredFromEquipment(removedEquipment) {
      const removedRequired = getNonChoiceRequired(removedEquipment);
      removedRequired.forEach((removedOption) => {
        // Only remove if the option is not required
        if (!this.allRequired[removedOption]) {
          // Remove potentially matching exterior colour
          if (
            this.selectedExteriorColour &&
            this.selectedExteriorColour.id === removedOption
          ) {
            return this.setExteriorColour(undefined);
          }
          // Remove potentially matching interior colour
          if (
            this.selectedInteriorColour &&
            this.selectedInteriorColour.id === removedOption
          ) {
            return this.setInteriorColour(undefined);
          }
          // Remove potentially matching optional equipment
          const matchingIndex = findIndexInList(
            removedOption,
            this.selectedOptionalEquipment
          );
          if (matchingIndex > -1) {
            return this.removeOptionalEquipment(matchingIndex);
          }
          // Remove potentially matching selected choices
          if (this.selectedChoices[removedOption]) {
            const equipment = mapToOption(
              removedOption,
              this.flatColourAndExtras
            );
            this.removeChoiceRequired(equipment);
          }
        }
      });
    },
    /**
     * Set any equipment required by a given equipment
     * @param equipment The equipment to set all required equipment
     */
    setRequiredInData(equipment) {
      const exterior = this.selectedExteriorColour || {};
      const interior = this.selectedInteriorColour || {};
      const nonChoiceRequired = getNonChoiceRequired(equipment);
      const { exteriorColours, interiorColours, optionalEquipment } =
        this.coloursAndExtras;

      nonChoiceRequired.forEach((requiredId) => {
        // Add as an exterior colour if it is found in the exterior colour list
        const matchingExterior = findIdInList(requiredId, exteriorColours);
        if (exterior.id !== requiredId && matchingExterior) {
          return this.setExteriorColour(matchingExterior);
        }
        // Add as an interior colour if it is found in the interior colour list
        const matchingInterior = findIdInList(requiredId, interiorColours);
        if (interior.id !== requiredId && matchingInterior) {
          return this.setInteriorColour(matchingInterior);
        }
        // Add as an optional equipment if it is found in the optional equipment list
        const matchingOption = findIdInList(requiredId, optionalEquipment);
        const hasOption = findIdInList(
          requiredId,
          this.selectedOptionalEquipment
        );
        if (matchingOption && !hasOption) {
          return this.addNewOptionalEquipment(matchingOption);
        }
      });
    },
    /**
     * Set customer options and create a quote progressing from the page when API requests are finished.
     * @returns {Promise<void>}
     */
    async next() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.updateCustomerOption({
        field: 'exteriorColour',
        value: this.selectedExteriorColour
      });
      this.updateCustomerOption({
        field: 'interiorColour',
        value: this.selectedInteriorColour
      });
      this.updateCustomerOption({
        field: 'optionalEquipment',
        value: [].concat(
          this.selectedOptionalEquipment,
          Object.values(this.selectedChoices)
        )
      });
      if (this.tempAdhocEquipment) {
        this.addAdhocEquipment(true);
      }
      try {
        // Create quote also saves the application
        await this.createQuote({
          componentContext: this,
          updateApplicationPaths: this.updateApplicationPaths
        });
        await this.$router.push({
          name: this.$RouteNames.dealerPrice,
          params: {
            ...this.$router.currentRoute.params,
            quoteId: this.quote.quoteId
          }
        });
      } catch (err) {}
    },
    /**
     * Maps a list of optional equipment to either the selectedOptionalEquipment or the selectedChoices list.
     * @param optionalEquipment The list of equipment to map
     */
    mapOptionalEquipment(optionalEquipment = []) {
      let choicesMap = {};
      let requiredMap = {};
      optionalEquipment.forEach((equipment) => {
        if (!equipment || !equipment.hasOwnProperty('id')) {
          return;
        }
        let required = getNonChoiceRequired(equipment);
        let choices = getChoiceRequired(equipment);
        required.forEach((id) => (requiredMap[id] = true));
        choices.forEach((choiceList) =>
          choiceList.forEach((id) => (choicesMap[id] = equipment.id))
        );
        if (!requiredMap[equipment.id]) {
          choicesMap[equipment.id]
            ? this.selectChoiceRequired(equipment, choicesMap[equipment.id])
            : (this.selectedOptionalEquipment = [
                ...this.selectedOptionalEquipment,
                equipment
              ]);
        }
      });
    }
  },
  mounted() {
    if (!this.customerSelectionIsNew) {
      return this.$router.push({
        name: this.$router.currentRoute.params.quoteId
          ? this.$RouteNames.dealerPrice
          : this.$RouteNames.carSearchVariant,
        params: { ...this.$router.currentRoute.params }
      });
    }

    this.getColoursAndExtras();
    const { exteriorColour, interiorColour, optionalEquipment } = this.quote;

    this.selectedExteriorColour =
      exteriorColour && (exteriorColour.id || exteriorColour.name)
        ? exteriorColour
        : this.customerOptions.exteriorColour;

    this.selectedInteriorColour =
      interiorColour && (interiorColour.id || interiorColour.name)
        ? interiorColour
        : this.customerOptions.interiorColour;

    this.mapOptionalEquipment(
      optionalEquipment.length
        ? optionalEquipment
        : this.customerOptions.optionalEquipment
    );

    this.tempAdhocEquipment = undefined;
  },
  components: {
    CarColourAndExtras: () =>
      import('../../components/page/car-colour-and-extras')
  }
};
</script>
